import {
  Box,
  FormLabel,
  MenuItem,
  Pagination,
  PaginationItem,
  type PaginationRenderItemParams,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material'
import {
  gridPageCountSelector,
  gridPaginationModelSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid'
import type { ChangeEvent } from 'react'
import { MAX_SEARCH_RESULTS, SEARCH_RESULTS_PER_PAGE } from '../../constants'
import { useSearch } from '../../hooks/useSearch'
import { DirectPageInput } from '../DirectPageInput'

export default function CustomPagination() {
  const apiRef = useGridApiContext()
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector)
  const pageCount = useGridSelector(apiRef, gridPageCountSelector)
  const rowCount = useGridSelector(apiRef, gridRowCountSelector)
  const { searchResults } = useSearch()
  const { details } = searchResults

  const page = paginationModel.page + 1

  // Check if we're at the max limit
  const exceedsMax = details.exceedsMax ?? false
  const maxPage = Math.ceil(MAX_SEARCH_RESULTS / paginationModel.pageSize)
  const effectivePageCount = exceedsMax
    ? Math.min(pageCount, maxPage)
    : pageCount

  // Calculate display values for "X-Y of Z"
  const hasRows = rowCount > 0
  const startRow = hasRows
    ? paginationModel.page * paginationModel.pageSize + 1
    : 0
  const endRow = hasRows
    ? Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount)
    : 0
  const totalDisplay = exceedsMax
    ? `${rowCount.toLocaleString()}+`
    : rowCount.toLocaleString()

  const renderItem = (params: PaginationRenderItemParams) => {
    return <PaginationItem {...params} />
  }

  const handleChange = (_event: ChangeEvent<unknown>, page: number) => {
    // Don't allow navigation beyond max limit
    if (page > effectivePageCount) {
      return
    }
    return apiRef.current.setPage(page - 1)
  }

  const handleDirectPageSubmit = (page: number): void => {
    apiRef.current.setPage(page - 1)
  }

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    if (event.target.value) {
      apiRef.current.setPageSize(event.target.value as number)
    }
  }

  return (
    <Box display="flex" alignItems="center" gap={4}>
      <Box display="flex" alignItems="center" gap={1}>
        <FormLabel sx={{ fontSize: 14 }}>Results per page:&nbsp;</FormLabel>
        <Select
          variant="standard"
          size="small"
          value={paginationModel.pageSize}
          onChange={handlePageSizeChange}
          sx={{ fontSize: 14 }}
        >
          {SEARCH_RESULTS_PER_PAGE.map((i) => (
            <MenuItem key={i} value={i}>
              {i}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box display="flex">
        <Typography variant="body2" color="text.secondary">
          {startRow.toLocaleString()}-{endRow.toLocaleString()} of{' '}
          {totalDisplay}
        </Typography>
      </Box>
      <DirectPageInput
        currentPage={page}
        pageCount={effectivePageCount}
        onPageSubmit={handleDirectPageSubmit}
      />
      <Pagination
        count={effectivePageCount}
        page={page}
        onChange={handleChange}
        renderItem={renderItem}
      />
    </Box>
  )
}
